import styled from 'styled-components';

export const Container = styled.div`
  max-width: ${props => props.maxWidth || '1200px'};
  margin: 0 auto;
  padding: 0 ${props => props.padding || '20px'};
  width: 100%;
  box-sizing: border-box;
  
  @media (max-width: 768px) {
    padding: 0 ${props => props.mobilePadding || '16px'};
  }
  
  ${props => props.fluid && `
    max-width: none;
  `}
`;

export const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin: -${props => props.gutter || '10px'};
  
  ${props => props.noWrap && `
    flex-wrap: nowrap;
  `}
  
  ${props => props.align && `
    align-items: ${props.align};
  `}
  
  ${props => props.justify && `
    justify-content: ${props.justify};
  `}
  
  @media (max-width: 768px) {
    ${props => props.mobileStack && `
      flex-direction: column;
      
      & > * {
        flex: 0 0 100% !important;
        max-width: 100% !important;
      }
    `}
  }
`;

export const Col = styled.div`
  flex: ${props => {
    if (props.auto) return '0 0 auto';
    if (props.span) return `0 0 ${(props.span / 12) * 100}%`;
    return '1';
  }};
  max-width: ${props => props.span ? `${(props.span / 12) * 100}%` : '100%'};
  padding: ${props => props.gutter || '10px'};
  box-sizing: border-box;
  
  /* Responsive breakpoints */
  @media (max-width: 1200px) {
    ${props => props.lg && `
      flex: 0 0 ${(props.lg / 12) * 100}%;
      max-width: ${(props.lg / 12) * 100}%;
    `}
  }
  
  @media (max-width: 992px) {
    ${props => props.md && `
      flex: 0 0 ${(props.md / 12) * 100}%;
      max-width: ${(props.md / 12) * 100}%;
    `}
  }
  
  @media (max-width: 768px) {
    ${props => props.sm && `
      flex: 0 0 ${(props.sm / 12) * 100}%;
      max-width: ${(props.sm / 12) * 100}%;
    `}
    
    ${props => !props.sm && !props.noMobileStack && `
      flex: 0 0 100%;
      max-width: 100%;
    `}
  }
  
  @media (max-width: 576px) {
    ${props => props.xs && `
      flex: 0 0 ${(props.xs / 12) * 100}%;
      max-width: ${(props.xs / 12) * 100}%;
    `}
  }
`;

export const Flex = styled.div`
  display: flex;
  align-items: ${props => props.$align || 'stretch'};
  justify-content: ${props => props.$justify || 'flex-start'};
  gap: ${props => props.$gap || '0'};
  flex-direction: ${props => props.$direction || 'row'};
  flex-wrap: ${props => props.$wrap || 'nowrap'};
  
  ${props => props.$fullHeight && `
    height: 100%;
  `}
  
  ${props => props.$fullWidth && `
    width: 100%;
  `}
  
  ${props => props.$inline && `
    display: inline-flex;
  `}
  
  ${props => props.$padding && `
    padding: ${props.$padding};
  `}
  
  ${props => props.$borderTop && `
    border-top: ${props.$borderTop};
  `}
  
  ${props => props.$borderColor && `
    border-color: ${props.theme.colors[props.$borderColor] || props.$borderColor};
  `}
  
  /* Additional props for compatibility */
  ${props => props.$alignItems && `align-items: ${props.$alignItems};`}
  ${props => props.$justifyContent && `justify-content: ${props.$justifyContent};`}
  ${props => props.$marginTop && `margin-top: ${props.$marginTop};`}
  ${props => props.$minHeight && `min-height: ${props.$minHeight};`}
  ${props => props.$width && `width: ${props.$width};`}
  
  @media (max-width: 768px) {
    flex-direction: ${props => props.$mobileDirection || props.$direction || 'row'};
    gap: ${props => props.$mobileGap || props.$gap || '0'};
    
    ${props => props.$mobilePadding && `
      padding: ${props.$mobilePadding};
    `}
    
    ${props => props.$mobileStack && `
      flex-direction: column;
    `}
  }
`;

export const Grid = styled.div`
  display: grid;
  grid-template-columns: ${props => 
    props.$columns 
      ? `repeat(${props.$columns}, 1fr)`
      : props.$template || 'repeat(auto-fit, minmax(250px, 1fr))'
  };
  gap: ${props => props.$gap || '20px'};
  
  ${props => props.$rows && `
    grid-template-rows: repeat(${props.$rows}, 1fr);
  `}
  
  ${props => props.$autoRows && `
    grid-auto-rows: ${props.$autoRows};
  `}
  
  ${props => props.$autoFlow && `
    grid-auto-flow: ${props.$autoFlow};
  `}
  
  @media (max-width: 1200px) {
    grid-template-columns: ${props => 
      props.$lgColumns 
        ? `repeat(${props.$lgColumns}, 1fr)`
        : props.$columns && props.$columns > 3
          ? `repeat(3, 1fr)`
          : props.$template || 'repeat(auto-fit, minmax(250px, 1fr))'
    };
  }
  
  @media (max-width: 992px) {
    grid-template-columns: ${props => 
      props.$mdColumns 
        ? `repeat(${props.$mdColumns}, 1fr)`
        : props.$columns && props.$columns > 2
          ? `repeat(2, 1fr)`
          : props.$template || 'repeat(auto-fit, minmax(250px, 1fr))'
    };
  }
  
  @media (max-width: 768px) {
    grid-template-columns: ${props => 
      props.$smColumns 
        ? `repeat(${props.$smColumns}, 1fr)`
        : props.$mobileStack !== false
          ? '1fr'
          : props.$template || 'repeat(auto-fit, minmax(250px, 1fr))'
    };
    gap: ${props => props.$mobileGap || '16px'};
  }
`;

export const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.$gap || '16px'};
  
  ${props => props.$align && `
    align-items: ${props.$align};
  `}
  
  ${props => props.$justify && `
    justify-content: ${props.$justify};
  `}
  
  ${props => props.$fullHeight && `
    height: 100%;
  `}
  
  ${props => props.$spacing && `
    & > *:not(:last-child) {
      margin-bottom: ${props.$spacing};
    }
  `}
`;

export const Spacer = styled.div`
  width: ${props => props.width || '100%'};
  height: ${props => props.height || '1px'};
  flex-shrink: 0;
  
  ${props => props.flex && `
    flex: 1;
  `}
`;

export const Divider = styled.hr`
  border: none;
  border-top: 1px solid ${props => props.theme.colors.border};
  margin: ${props => props.$margin || '20px 0'};
  width: 100%;
  
  ${props => props.$vertical && `
    border-top: none;
    border-left: 1px solid ${props.theme.colors.border};
    height: 100%;
    width: 1px;
    margin: 0 ${props.$margin || '20px'};
  `}
`;

export const Section = styled.section`
  padding: ${props => props.padding || '40px 0'};
  
  ${props => props.background && `
    background: ${props.background};
  `}
  
  ${props => props.centered && `
    text-align: center;
  `}
  
  @media (max-width: 768px) {
    padding: ${props => props.mobilePadding || '24px 0'};
  }
`;

export const Box = styled.div`
  padding: ${props => props.$p || '0'};
  margin: ${props => props.$m || '0'};
  
  ${props => props.$pt && `padding-top: ${props.$pt};`}
  ${props => props.$pb && `padding-bottom: ${props.$pb};`}
  ${props => props.$pl && `padding-left: ${props.$pl};`}
  ${props => props.$pr && `padding-right: ${props.$pr};`}
  ${props => props.$px && `padding-left: ${props.$px}; padding-right: ${props.$px};`}
  ${props => props.$py && `padding-top: ${props.$py}; padding-bottom: ${props.$py};`}
  
  ${props => props.$mt && `margin-top: ${props.$mt};`}
  ${props => props.$mb && `margin-bottom: ${props.$mb};`}
  ${props => props.$ml && `margin-left: ${props.$ml};`}
  ${props => props.$mr && `margin-right: ${props.$mr};`}
  ${props => props.$mx && `margin-left: ${props.$mx}; margin-right: ${props.$mx};`}
  ${props => props.$my && `margin-top: ${props.$my}; margin-bottom: ${props.$my};`}
  
  ${props => props.$width && `width: ${props.$width};`}
  ${props => props.$height && `height: ${props.$height};`}
  ${props => props.$maxWidth && `max-width: ${props.$maxWidth};`}
  ${props => props.$maxHeight && `max-height: ${props.$maxHeight};`}
  
  ${props => props.$bg && `background: ${props.$bg};`}
  ${props => props.$color && `color: ${props.$color};`}
  ${props => props.$border && `border: ${props.$border};`}
  ${props => props.$borderRadius && `border-radius: ${props.$borderRadius};`}
  ${props => props.$borderTop && `border-top: ${props.$borderTop};`}
  ${props => props.$borderBottom && `border-bottom: ${props.$borderBottom};`}
  ${props => props.$borderLeft && `border-left: ${props.$borderLeft};`}
  ${props => props.$borderRight && `border-right: ${props.$borderRight};`}
  
  ${props => props.$textAlign && `text-align: ${props.$textAlign};`}
  ${props => props.$fontSize && `font-size: ${props.$fontSize};`}
  ${props => props.$fontWeight && `font-weight: ${props.$fontWeight};`}
  
  ${props => props.$position && `position: ${props.$position};`}
  ${props => props.$top && `top: ${props.$top};`}
  ${props => props.$bottom && `bottom: ${props.$bottom};`}
  ${props => props.$left && `left: ${props.$left};`}
  ${props => props.$right && `right: ${props.$right};`}
  ${props => props.$zIndex && `z-index: ${props.$zIndex};`}
  
  ${props => props.$overflow && `overflow: ${props.$overflow};`}
  ${props => props.$cursor && `cursor: ${props.$cursor};`}
  
  ${props => props.$display && `display: ${props.$display};`}
  ${props => props.$flexDirection && `flex-direction: ${props.$flexDirection};`}
  ${props => props.$padding && `padding: ${props.$padding};`}
`;
